import { useNavigate } from 'react-router-dom';
import { getSeasonRounds } from '../core/api';
import { SEASON_ID } from '../core/config';
import { useT } from '../core/lexicon';
import { ordinal, seq } from '../core/util';
import { MediaGlyph } from '../core/sprite';
import { useAsync } from '../core/useAsync';
import { Screen, TopBar, CenterNote } from './Layout';
import type { SeasonRound } from '../core/types';

type Group = 'past' | 'now' | 'next' | 'later';

/**
 * The whole season, past and future (spec §20 / api.md). Future rounds show theme,
 * note and media type only — a round has no dates until the previous one closes.
 * Showing the season ahead is deliberate: people think about a theme for days.
 */
export function Rounds() {
  const t = useT();
  const nav = useNavigate();
  const load = useAsync<SeasonRound[]>(() => getSeasonRounds(SEASON_ID), []);

  const title = t('schedule.name');
  if (load.loading) {
    return (
      <Screen>
        <TopBar title={title} />
        <CenterNote>
          <div className="spin">LOADING…</div>
        </CenterNote>
      </Screen>
    );
  }
  const rounds = load.data ?? [];
  if (rounds.length === 0) {
    return (
      <Screen>
        <TopBar title={title} />
        <CenterNote title={t('schedule.empty').toUpperCase()} />
      </Screen>
    );
  }

  const dividerLabel: Record<Group, string> = {
    past: t('schedule.past'),
    now: t('schedule.now'),
    next: t('schedule.next'),
    later: t('schedule.later'),
  };

  let lastGroup: Group | null = null;
  let scheduledSeen = false;

  return (
    <Screen>
      <TopBar title={title} sub={t('season.label', { n: SEASON_ID }).toUpperCase()} right={`${rounds.length}`} />
      <div className="list">
        {rounds.map((r) => {
          let group: Group;
          if (r.phase === 'CLOSED') group = 'past';
          else if (r.phase === 'SUBMITTING' || r.phase === 'VOTING') group = 'now';
          else {
            group = scheduledSeen ? 'later' : 'next';
            scheduledSeen = true;
          }

          const showDivider = group !== lastGroup && !(lastGroup === 'next' && group === 'later');
          if (group !== lastGroup) lastGroup = group;

          const clickable = group === 'past' || group === 'now';
          const go = () => nav(group === 'past' ? '/results' : '/');

          return (
            <div key={r.round_id}>
              {showDivider ? <div className="divider">{dividerLabel[group].toUpperCase()}</div> : null}
              <div
                className={`srow${group === 'now' ? ' now' : group === 'later' ? ' later' : ''}`}
                style={clickable ? { cursor: 'pointer' } : undefined}
                onClick={clickable ? go : undefined}
              >
                <div className="seq">{seq(r.sequence_no)}</div>
                <div>
                  <div className="s-theme">
                    <MediaGlyph type={r.media_type} />
                    {r.theme}
                  </div>
                  {r.theme_note ? <div className="note">{r.theme_note}</div> : null}
                  {r.phase === 'CLOSED' && r.winner ? (
                    <div className="meta">
                      {t('schedule.winner', { name: r.winner.display_name })}
                      {r.your_placing ? ` · ${t('schedule.yours', { n: ordinal(r.your_placing) })}` : ''}
                    </div>
                  ) : null}
                </div>
                {group === 'past' ? (
                  <div className="stag">{t('phase.reveal.name').toUpperCase()}</div>
                ) : group === 'now' ? (
                  <div className="stag">{t('schedule.now').toUpperCase()}</div>
                ) : group === 'next' ? (
                  <div className="stag">{t('schedule.next').toUpperCase()}</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Screen>
  );
}
